import React, { useCallback, useEffect, useRef, useState } from 'react';
import IPage from '../../../interfaces/IPage';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Container, Form } from './MedicalCertificateForm.styled';
import PageTitle from '../../../components/page-title/PageTitle';
import { DatePicker, LocalizationProvider, TimePicker, ptBR } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { FormLabel, InputAdornment, debounce, FormHelperText } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MdOutlineAssignmentReturn } from 'react-icons/md';
import IFormError from '../../../interfaces/IFormError';
import IMedicalCertificate from '../../../interfaces/IMedicalCertificate';
import IEmployee from '../../../interfaces/IEmployee';
import { useEmployeeService } from '../../../hooks/services/useEmployeeService';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';
import useCpfCnpjFormat from '../../../hooks/util/useCpfCnpjFormat';
import { useTypeDocumentService } from '../../../hooks/services/useTypeDocumentService';
import ITypeDocument from '../../../interfaces/ITypeDocument';
import AppError from '../../../errors/AppError';
import { useProfessionalService } from '../../../hooks/services/useProfessionalService';
import IProfessionalType from '../../../interfaces/IProfessionalType';
import moment from 'moment';
import * as yup from 'yup';
import InputMask from 'react-input-mask';
import { useValidateSchema } from '../../../hooks/providers/useValidateSchema';
import { useMedicalCertificateService } from '../../../hooks/services/useMedicalCertificateService';
import { LoadingButton } from '@mui/lab';
import MedicalCertificateFormCid from './MedicalCertificateFormCid';
import MedicalCertificateFormAttachments from './MedicalCertificateFormAttachments';
import { AiFillCloseCircle } from 'react-icons/ai';
import { useFocus } from '../../../hooks/util/useFocus';

const initialMedicalCertificate: IMedicalCertificate = {
  typeDocumentId: 0,
  employeeId: 0,
  clinicName: '',
  professionalTypeId: 0,
  registerNumber: '',
  professionalName: '',
  documentDate: null,
  typeTime: 'DAY',
  startTime: '',
  endTime: '',
  timeQuantity: 0,
  diseaseClassifications: [],
  email: '',
  phoneNumber: '',
  status: 'PENDING',
  comments: '',
  notes: '',
  origin: '',
  statusIntegration: '',
  datetimeIntegraton: null,
  confirmed: false,
  confirmedAt: null,
  professionalTypeName: '',
  professionalTypeOrganization: '',
  professionalState: '',
  errorTitle: '',
  errorMessage: '',
  justifiedDays: 0,
};

const states = [
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
];

interface IProps extends IPage {
  closeDialog?: () => void;
  isOpen?: boolean;
  medicalCertificateId?: string;
  handleRequest?: () => Promise<void>;
  employeeId?: string;
}
const MedicalCertificateForm = (props: IProps) => {
  window.document.title = props.title;

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<IFormError | null>(null);
  const [medicalCertificate, setMedicalCertificate] = useState<IMedicalCertificate>(initialMedicalCertificate);
  const [employee, setEmployee] = useState<IEmployee | null>(null);
  const [documentTypes, setDocumentTypes] = useState<ITypeDocument[]>([]);
  const [actionLabel, setActionLabel] = useState('INCLUSÃO');
  const [professionalTypes, setProfessionalTypes] = useState<IProfessionalType[]>([]);
  const [formatForRecordingNumberOfHoursId, setFormatForRecordingNumberOfHoursId] = useState(1);
  const [timerQuantity, setTimerQuantity] = useState('');
  const [startTimer, setStartTimer] = useState<moment.Moment | null>(moment(medicalCertificate.startTime));
  const [endTimer, setEndTimer] = useState<moment.Moment | null>(moment(medicalCertificate.startTime));

  const buttonRef = useRef<HTMLInputElement>();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const employeeService = useEmployeeService();
  const documentTypeService = useTypeDocumentService();
  const professionalService = useProfessionalService();
  const medicalCertificateService = useMedicalCertificateService();

  const toastr = useToastrVanila();
  const { formatCpf } = useCpfCnpjFormat();
  const vallidate = useValidateSchema();
  const focus = useFocus();

  const getFormatForRecordingNumberOfHours = async () => {
    await medicalCertificateService.getFormatForRecordingNumberOfHours().then(resource => {
      setFormatForRecordingNumberOfHoursId(resource.formatForRecordingNumberOfHoursId);
    });
  };

  useEffect(() => {
    const action = location.pathname.split('/')[location.pathname.split('/').length - 1] as 'edit' | 'new';

    loadData();

    if (action === 'edit' || props.medicalCertificateId) {
      setActionLabel('EDIÇÃO');
    } else {
      setActionLabel('INCLUSÃO');
    }

    getFormatForRecordingNumberOfHours();
  }, []);

  const loadEmployee = async (employeeId: string): Promise<void> => {
    await employeeService
      .listById(parseInt(employeeId))
      .then(result => {
        setEmployee(result);
        setMedicalCertificate({ ...medicalCertificate, employeeId: parseInt(`${params.employeeId}`) });
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const loadProfessionalType = async (): Promise<void> => {
    await professionalService
      .listTypes()
      .then(result => {
        setProfessionalTypes(result);
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listProfessional = useCallback(
    debounce(async (register: string, state: string, medicalCertificate: IMedicalCertificate) => {
      if (register && state) {
        setLoading(true);
        await professionalService
          .listByRegisterEmState(register, state)
          .then(professional => {
            setMedicalCertificate({
              ...medicalCertificate,
              registerNumber: register,
              professionalName: professional.name,
            });
            setLoading(false);
          })
          .catch(error => {
            setLoading(false);
            if (error.message) {
              toastr.error(error.message);
            }
          });
      }
    }, 1000),
    [],
  );

  const loadDocumentType = async (): Promise<void> => {
    await documentTypeService
      .list(300, 1, 'description', 'asc')
      .then(result => {
        setDocumentTypes(result?.data);
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const loadData = async (): Promise<void> => {
    setLoading(true);
    try {
      const employeeId = props.employeeId || params.employeeId || '0';
      await loadEmployee(employeeId);
      await loadDocumentType();
      await loadProfessionalType();
      const id = props.medicalCertificateId || params.id || '0';
      if (id && id !== '0') {
        await loadMedicalCertificate(id);
      }
      setLoading(false);
    } catch {
      setLoading(false);
      toastr.error('Ocorreu um erro ao carregar os dados. Tente novamente mais tarde.');
    }
  };

  const incrementDay = () => {
    const time = parseInt(`${medicalCertificate.timeQuantity}`)
      ? parseInt(`${medicalCertificate.timeQuantity}`) + 1
      : 1;
    const startTime = medicalCertificate.documentDate;
    const endTime = moment(medicalCertificate.documentDate)
      .add(time, 'days')
      .format('yyyy-MM-DD');
    setMedicalCertificate({ ...medicalCertificate, timeQuantity: time, startTime, endTime });
  };

  const decrementDay = () => {
    const time =
      medicalCertificate.timeQuantity && parseInt(`${medicalCertificate.timeQuantity}`) > 0
        ? parseInt(`${medicalCertificate.timeQuantity}`) - 1
        : 0;

    const startTime = medicalCertificate.documentDate;
    const endTime = moment(medicalCertificate.documentDate)
      .add(time, 'days')
      .format('yyyy-MM-DD');
    setMedicalCertificate({ ...medicalCertificate, timeQuantity: time, startTime, endTime });
    setMedicalCertificate({ ...medicalCertificate, timeQuantity: time });
  };

  const handleStartTime = (startTime: string) => {
    if (!medicalCertificate.documentDate) {
      toastr.warning('Antes informe a data do documento.');
      return;
    }
    const startMinute = moment(startTime).minute() + moment(startTime).hour() * 60;
    const start = moment(medicalCertificate.documentDate)
      .startOf('day')
      .add(startMinute, 'minutes')
      .format();
    setMedicalCertificate({ ...medicalCertificate, startTime: start });
  };

  const handleEndTime = (endTime: string) => {
    if (!medicalCertificate.documentDate) {
      toastr.warning('Antes informe a data do documento.');
      return;
    }

    const endMinute = moment(endTime).minute() + moment(endTime).hours() * 60;
    const end = moment(medicalCertificate.documentDate)
      .startOf('day')
      .add(endMinute, 'minutes')
      .format();

    const quantity = Math.round((moment(end).diff(moment(medicalCertificate.startTime), 'minute') / 60) * 100) / 100;

    const newData = {
      endTime: end,
      timeQuantity: quantity,
    };

    setMedicalCertificate({ ...medicalCertificate, ...newData });
  };

  const clinicNameValidation = (
    clinicName: string | undefined | null,
    schema: yup.TestContext,
  ): yup.ValidationError | boolean => {
    const documentType = documentTypes.find(d => medicalCertificate.typeDocumentId === d.id);

    if (!clinicName && (documentType?.crmRequired || !documentType)) {
      return schema.createError({ message: `Campo obrigatório para esse tipo de documento.` });
    }

    if (`${clinicName}`.split('').length === 0 && (documentType?.hospitalRequired || !documentType)) {
      return schema.createError({ message: `Campo obrigatório para esse tipo de documento.` });
    }
    return true;
  };

  const professionalStateValidation = (
    professionalState: string | undefined | null,
    schema: yup.TestContext,
  ): yup.ValidationError | boolean => {
    const documentType = documentTypes.find(d => medicalCertificate.typeDocumentId === d.id);

    if (!professionalState && (documentType?.crmRequired || !documentType)) {
      return schema.createError({ message: `Campo obrigatório para esse tipo de documento.` });
    }

    if (`${professionalState}`.split('').length === 0 && (documentType?.crmRequired || !documentType)) {
      return schema.createError({ message: `Campo obrigatório para esse tipo de documento.` });
    }
    return true;
  };

  const registerNumberValidation = (
    registerNumber: string | undefined | null,
    schema: yup.TestContext,
  ): yup.ValidationError | boolean => {
    const documentType = documentTypes.find(d => medicalCertificate.typeDocumentId === d.id);

    if (!registerNumber && (documentType?.crmRequired || !documentType)) {
      return schema.createError({ message: `Campo obrigatório para esse tipo de documento.` });
    }

    if (`${registerNumber}`.split('').length === 0 && (documentType?.crmRequired || !documentType)) {
      return schema.createError({ message: `Campo obrigatório para esse tipo de documento.` });
    }

    return true;
  };
  const professionalNameValidation = (
    professionalName: string | undefined | null,
    schema: yup.TestContext,
  ): yup.ValidationError | boolean => {
    const documentType = documentTypes.find(d => medicalCertificate.typeDocumentId === d.id);

    if (!professionalName && (documentType?.crmRequired || !documentType)) {
      return schema.createError({ message: `Campo obrigatório para esse tipo de documento.` });
    }
    if (`${professionalName}`.split('').length === 0 && (documentType?.crmRequired || !documentType)) {
      return schema.createError({ message: `Campo obrigatório para esse tipo de documento.` });
    }
    return true;
  };

  const professionalTypeValidation = (
    professionalType: string | undefined | null,
    schema: yup.TestContext,
  ): yup.ValidationError | boolean => {
    const documentType = documentTypes.find(d => medicalCertificate.typeDocumentId === d.id);

    if (!professionalType && (documentType?.crmRequired || !documentType)) {
      return schema.createError({ message: `Campo obrigatório para esse tipo de documento.` });
    }

    if (`${professionalType}`.split('').length === 0 && (documentType?.crmRequired || !documentType)) {
      return schema.createError({ message: `Campo obrigatório para esse tipo de documento.` });
    }
    return true;
  };

  const dateValidation = (date: string, schema: yup.TestContext): yup.ValidationError | boolean => {
    if (!date) {
      return schema.createError({ message: `Campo obrigatório` });
    }
    if (date === 'null') {
      return schema.createError({ message: `Campo obrigatório` });
    }

    if (date === 'Invalid date') {
      return schema.createError({ message: `Data inválida` });
    }
    return true;
  };

  const cleanPhoneNumber = (phone: string): string => {
    return phone
      .replaceAll(' ', '')
      .replaceAll('-', '')
      .replaceAll('(', '')
      .replaceAll(')', '')
      .replaceAll('+', '');
  };

  const schema = yup.object({
    clinicName: yup
      .string()
      .nullable()
      .test((clinicName, schema) => clinicNameValidation(clinicName, schema)),
    typeDocumentId: yup
      .number()
      .min(1)
      .required('Necessário informar o tipo de documento'),
    professionalState: yup
      .string()
      .nullable()
      .test((professionalState, schema) => professionalStateValidation(professionalState, schema)),
    professionalTypeId: yup
      .string()
      .nullable()
      .test((professionalTypeId, schema) => professionalTypeValidation(professionalTypeId, schema)),
    registerNumber: yup
      .string()
      .nullable()
      .test((registerNumber, schema) => registerNumberValidation(registerNumber, schema)),
    professionalName: yup
      .string()
      .nullable()
      .test((professionalName, schema) => professionalNameValidation(professionalName, schema)),
    documentDate: yup
      .string()
      .nullable(true)
      .test((date, schema) => dateValidation(`${date}`, schema)),
    timeQuantity: yup
      .number()
      .min(0.01, 'Valor deve ser maior do que zero')
      .max(9999, 'O máximo permitido é 9999')
      .required('Campo obrigatório'),
    justifiedDays: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when('alerts', {
        is: (alerts: string[]) => alerts?.some(a => a === 'INSS'),
        then: schema =>
          schema
            .min(0, 'Valor deve ser maior ou igual a zero')
            .max(15, 'O máximo permitido é 15')
            .required('Campo obrigatório'),
        otherwise: schema => schema.notRequired().nullable(),
      }),
    startTime: yup
      .string()
      .nullable(true)
      .test((date, schema) => dateValidation(`${date}`, schema)),
    endTime: yup
      .string()
      .nullable(true)
      .test((date, schema) => {
        const endTime = moment(medicalCertificate.endTime);
        const startTime = moment(medicalCertificate.startTime);
        if (!endTime?.isAfter(startTime) && !endTime?.isSame(startTime) && !endTimer) {
          return schema.createError({ message: `Deve ser maior ou igual ao início` });
        }

        if (endTime?.isSame(startTime)) {
          return schema.createError({ message: `As horas inicial e final devem ser diferentes` });
        }

        return dateValidation(`${date}`, schema);
      }),
    email: yup
      .string()
      .email('Favor informar um e-mail válido')
      .test((email = '', schema) => {
        if (!schema.parent.phoneNumber && !email) {
          return schema.createError({ message: `Telefone ou e-mail deve ser informado.` });
        }
        return true;
      }),
    phoneNumber: yup.string().test((phoneNumber: string = '', schema: yup.TestContext) => {
      const phone = cleanPhoneNumber(phoneNumber);

      if (phone.split('').length > 0 && phone.split('').length < 11) {
        return schema.createError({ message: 'O número não está correto.' });
      }
      if (!phoneNumber && !schema.parent.email) {
        return schema.createError({ message: `Telefone ou e-mail deve ser informado.` });
      }
      return true;
    }),
  });

  const instantiateMedicalCertificate = (medicalCertificate: IMedicalCertificate): IMedicalCertificate => {
    const resource: IMedicalCertificate = {
      ...(medicalCertificate.id && { id: medicalCertificate.id }),
      typeDocumentId: medicalCertificate.typeDocumentId,
      employeeId: medicalCertificate.employeeId,
      ...(medicalCertificate.employee && { employee: medicalCertificate.employee }),
      ...(medicalCertificate.typeDocument && { typeDocument: medicalCertificate.typeDocument }),
      clinicName: medicalCertificate.clinicName,
      professionalTypeId: medicalCertificate.professionalTypeId,
      registerNumber: medicalCertificate.registerNumber,
      professionalName: medicalCertificate.professionalName,
      documentDate: medicalCertificate.documentDate,
      typeTime: medicalCertificate.typeTime,
      startTime: moment(medicalCertificate.startTime).format(),
      endTime: moment(medicalCertificate.endTime).format(),
      timeQuantity: medicalCertificate.timeQuantity,
      diseaseClassifications: medicalCertificate.diseaseClassifications,
      email: medicalCertificate.email,
      phoneNumber: medicalCertificate.phoneNumber,
      status: 'PENDING',
      comments: medicalCertificate.comments,
      notes: medicalCertificate.notes,
      origin: 'BACKOFFICE',
      statusIntegration: null,
      datetimeIntegraton: null,
      confirmed: medicalCertificate.confirmed,
      confirmedAt: medicalCertificate.confirmedAt,
      professionalTypeName: medicalCertificate.professionalTypeName,
      professionalTypeOrganization: medicalCertificate.professionalTypeOrganization,
      professionalState: medicalCertificate.professionalState,
      protocolNumber: medicalCertificate.protocolNumber,
      justifiedDays: medicalCertificate.justifiedDays,
      alerts: medicalCertificate.alerts || [],
      ...(medicalCertificate.createdAt && { createdAt: medicalCertificate.createdAt }),
      ...(medicalCertificate.updatedAt && { updatedAt: medicalCertificate.updatedAt }),
      ...(medicalCertificate.deletedAt && { deletedAt: medicalCertificate.deletedAt }),
      isDraft: false,
      errorTitle: medicalCertificate.errorTitle,
      errorMessage: medicalCertificate.errorMessage,
    };
    if (medicalCertificate.attachments && medicalCertificate.attachments?.length > 0) {
      resource.attachments = medicalCertificate.attachments;
    } else {
      resource.attachments = [];
    }

    return resource;
  };

  function decimalParaString(decimal: number): string {
    const horas = Math.floor(decimal);
    const minutos = Math.round((decimal - horas) * 60);
    return `${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}`;
  }

  const loadMedicalCertificate = async (id: string) => {
    await medicalCertificateService
      .listById(id)
      .then(result => {
        const time = result.typeTime === 'DAY' ? result?.timeQuantity / 24 : result?.timeQuantity;
        setMedicalCertificate({ ...result, timeQuantity: time });
        setStartTimer(moment(result?.startTime));
        setEndTimer(moment(result?.endTime));
        setTimerQuantity(String(decimalParaString(result?.timeQuantity)));
      })
      .catch(error => {
        toastr.error('Ocorreu um erro ao carregar os dados da página. Tente novamente mais tarde.');
      });
  };

  const create = async (medicalCertificate: IMedicalCertificate) => {
    setLoading(true);
    await medicalCertificateService
      .create(medicalCertificate)
      .then(async result => {
        setLoading(false);
        await loadMedicalCertificate(`${result.id}`);
        navigate(`/cadastrar-documento/${result.id}/${employee?.id}/edit`);
        toastr.success('Documento salvo com sucesso.');
        setActionLabel('EDIÇÃO');
      })
      .catch(error => {
        setLoading(false);
        toastr.error('Ocorreu um erro ao cadastrar o documento. Tente novamente mais tarde.');
      });
  };

  const update = async (id: string, medicalCertificate: IMedicalCertificate) => {
    setLoading(true);
    await medicalCertificateService
      .update(id, medicalCertificate)
      .then(async () => {
        await loadMedicalCertificate(id);
        setLoading(false);

        toastr.success('Atualizado salvo com sucesso.');
        if (props.medicalCertificateId) {
          props.closeDialog && props.closeDialog();
        }
      })
      .catch(() => {
        setLoading(false);
        toastr.error('Ocorreu um erro ao atualizar o documento. Tente novamente mais tarde.');
      });
  };

  const handleTimeQuantity = async (timeQuantity: string) => {
    setMedicalCertificate({ ...medicalCertificate, timeQuantity: parseInt(timeQuantity) });
  };

  const handleJustifiedDays = (justifiedDays: string) => {
    const parsed = justifiedDays === '' ? undefined : Number(justifiedDays);
    setMedicalCertificate(prev => ({
      ...prev,
      justifiedDays: parsed,
    }));
  };

  const handleSubmit = async () => {
    const resource = { ...medicalCertificate };

    if (
      moment.duration(endTimer?.diff(startTimer)).asHours() >= 23 &&
      resource.typeTime === 'HOUR' &&
      formatForRecordingNumberOfHoursId === 2
    ) {
      toastr.error('Informe uma quantidade de horas menor ou igual a 23.');
      return;
    }

    if (resource.typeTime === 'DAY') {
      const startMinutes = moment(resource.startTime).minute() + moment(resource.startTime).hour() * 60;
      const startTime = moment(resource.documentDate)
        .startOf('day')
        .add(startMinutes, 'minutes')
        .format();
      const endTime = moment(startTime)
        .add(resource.timeQuantity, 'day')
        .subtract(1, 'minute')
        .format();
      resource.timeQuantity = resource.timeQuantity * 24;
      resource.startTime = startTime;
      resource.endTime = endTime;
      if(resource.timeQuantity >= 15 * 24) {
        resource.justifiedDays = 15;
      }else {
        resource.justifiedDays = 0;
      }
    } else {
      const startMinutes = moment(resource.startTime).minute() + moment(resource.startTime).hour() * 60;
      const startTime = moment(resource.documentDate)
        .startOf('day')
        .add(startMinutes, 'minutes')
        .format();

      const endMinutes = moment(resource.endTime).minute() + moment(resource.endTime).hour() * 60;
      const endTime = moment(resource.documentDate)
        .startOf('day')
        .add(endMinutes, 'minutes')
        .format();

      resource.startTime = formatForRecordingNumberOfHoursId === 2 ? String(startTimer?.format()) : startTime;
      resource.endTime = formatForRecordingNumberOfHoursId === 2 ? String(endTimer?.format()) : endTime;

      resource.timeQuantity = Number(
        moment
          .duration(moment(resource.endTime)?.diff(resource.startTime))
          .asHours()
          .toFixed(2),
      );
    }

    const result = await vallidate.validate(schema, { ...resource });
    if (!result.isValid) {
      setErrors({ ...result.errors });
      return;
    } else {
      setErrors(null);
    }

    if (!resource.id) {
      await create(instantiateMedicalCertificate(resource));
    } else {
      await update(`${resource.id}`, instantiateMedicalCertificate(resource));
    }
  };

  const handleChangeTimeQuantity = useCallback(
    (timerQuantity: string) => {
      const [hour, minute] = timerQuantity.split(':');

      if (startTimer) {
        const time = moment(startTimer.format())
          .add(Number(hour), 'hours')
          .add(Number(minute), 'minutes');

        setEndTimer(time);
      }
    },
    [timerQuantity, startTimer],
  );

  return (
    <Container>
      <PageTitle title={`${actionLabel} DE DOCUMENTO`}>
        {!props.medicalCertificateId && (
          <Tooltip title="Voltar para listagem" placement="left-start">
            <IconButton onClick={() => navigate(`/cadastrar-documento`)} color="inherit">
              <MdOutlineAssignmentReturn />
            </IconButton>
          </Tooltip>
        )}
        {props.medicalCertificateId && (
          <Tooltip title="Fechar janela" placement="left-start">
            <IconButton onClick={() => props.closeDialog && props.closeDialog()}>
              <AiFillCloseCircle />
            </IconButton>
          </Tooltip>
        )}
      </PageTitle>
      {loading && <LinearProgress />}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ fontSize: 17, marginTop: 2, marginBottom: 2 }} component="div">
          Empresa: {employee?.company?.name}
        </Typography>

        <Typography sx={{ fontSize: 17, marginTop: 2, marginBottom: 2 }} component="div">
          Colaborador: {employee?.name}
        </Typography>

        <Typography sx={{ fontSize: 17, marginTop: 2, marginBottom: 2 }} component="div">
          CPF: {formatCpf(`${employee?.cpf}`)}
        </Typography>
      </Box>

      <Form>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={Boolean(errors?.typeDocumentId)}>
              <InputLabel id="document-type">Tipo de Documento</InputLabel>
              <Select
                labelId="document-type"
                id="document-type-select"
                value={medicalCertificate.typeDocumentId || ''}
                label="Tipo de Documento"
                onChange={e =>
                  setMedicalCertificate({ ...medicalCertificate, typeDocumentId: parseInt(`${e.target.value}`) })
                }
              >
                {documentTypes.map(document => {
                  return (
                    <MenuItem value={document.id} key={document.id}>
                      {document.description}
                    </MenuItem>
                  );
                })}
              </Select>
              <FormHelperText>{errors?.typeDocumentId}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Local"
              placeholder="Informe o local"
              value={medicalCertificate?.clinicName || ''}
              onChange={e => setMedicalCertificate({ ...medicalCertificate, clinicName: e.target.value })}
              error={Boolean(errors?.clinicName)}
              helperText={errors?.clinicName}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={Boolean(errors?.professionalTypeId)}>
              <InputLabel id="professional-type">Tipo de Profissional</InputLabel>
              <Select
                labelId="professional-type"
                id="professional-type-select"
                value={medicalCertificate.professionalTypeId || ''}
                label="Tipo de Profissional"
                onChange={e =>
                  setMedicalCertificate({
                    ...medicalCertificate,
                    professionalTypeId: parseInt(`${e.target.value}`),
                    professionalTypeName: `${professionalTypes.find(t => t.id === e.target.value)?.name}`,
                    professionalTypeOrganization: `${professionalTypes.find(t => t.id === e.target.value)?.organization
                      }`,
                  })
                }
              >
                {professionalTypes.map(professionalType => {
                  return (
                    <MenuItem value={professionalType.id} key={professionalType.id}>
                      {professionalType.name}
                    </MenuItem>
                  );
                })}
              </Select>
              <FormHelperText>{errors?.professionalTypeId}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={Boolean(errors?.professionalState)}>
              <InputLabel id="professional-state">Estado do Profissional</InputLabel>
              <Select
                labelId="professional-state"
                id="professional-state-select"
                value={medicalCertificate.professionalState || ''}
                label="Estado do Profissional"
                onChange={e => setMedicalCertificate({ ...medicalCertificate, professionalState: e.target.value })}
                MenuProps={{ sx: { maxHeight: '300px' } }}
              >
                {states.map((state, i) => {
                  return (
                    <MenuItem value={state} key={i}>
                      {state}
                    </MenuItem>
                  );
                })}
              </Select>
              <FormHelperText>{errors?.professionalState}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              value={String(medicalCertificate?.registerNumber) || ''}
              onChange={e => {
                if (medicalCertificate.professionalTypeId === 1) {
                  listProfessional(e.target.value, medicalCertificate.professionalState, medicalCertificate);
                }

                setMedicalCertificate({ ...medicalCertificate, registerNumber: e.target.value });
              }}
              label={` ${medicalCertificate.professionalTypeOrganization || 'Número Registro'}`}
              placeholder={` ${medicalCertificate.professionalTypeOrganization || 'Número Registro'}`}
              error={Boolean(errors?.registerNumber)}
              helperText={errors?.registerNumber}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nome do profissional"
              placeholder="Nome do profissional"
              value={medicalCertificate?.professionalName}
              onChange={e => setMedicalCertificate({ ...medicalCertificate, professionalName: e.target.value })}
              error={Boolean(errors?.professionalName)}
              helperText={errors?.professionalName}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider
              dateAdapter={AdapterMoment}
              localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
            >
              <DatePicker
                value={medicalCertificate.documentDate}
                label="Data do documento"
                onChange={async e => {
                  setMedicalCertificate({ ...medicalCertificate, documentDate: moment(e).format('yyyy-MM-DD') });

                  const isOverlaped = await medicalCertificateService.isOverlap(
                    medicalCertificate.employeeId,
                    String(moment(e)),
                  );

                  if (isOverlaped) {
                    toastr.warning(
                      `Sobreposição de Período: Existe um documento vigente neste período. Por favor, confirme se a data do documento está correta.`,
                    );
                  }
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    error={Boolean(errors?.documentDate)}
                    placeholder="Data do documento"
                    helperText={errors?.documentDate}
                    autoFocus
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl>
              <FormLabel id="demo-row-radio-buttons-group-label">Tipo de intervalo</FormLabel>
              <RadioGroup aria-labelledby="demo-radio-buttons-group-label" name="radio-buttons-group" row>
                <FormControlLabel
                  control={
                    <Radio
                      checked={medicalCertificate.typeTime === 'DAY'}
                      onClick={() =>
                        setMedicalCertificate({
                          ...medicalCertificate,
                          typeTime: 'DAY',
                          startTime: null,
                          endTime: null,
                          timeQuantity: 0,
                        })
                      }
                    />
                  }
                  label="Dias"
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={medicalCertificate.typeTime === 'HOUR'}
                      onClick={() =>
                        setMedicalCertificate({
                          ...medicalCertificate,
                          typeTime: 'HOUR',
                          startTime: '',
                          endTime: '',
                          timeQuantity: 0,
                        })
                      }
                    />
                  }
                  label="Horas"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          {medicalCertificate.typeTime === 'DAY' ? (
            <Grid container xs={12} md={12} sx={{ display: 'flex', ml: 2, mt: 1 }}>
              <Grid item xs={12} md={6} sx={{ pr: 1 }}>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="Quantidade de dias"
                  label="Quantidade de dias"
                  value={medicalCertificate.timeQuantity}
                  onChange={e => {
                    if (Number(e.target.value) >= 0) {
                      handleTimeQuantity(`${e.target.value}`);
                    }
                  }}
                  error={Boolean(errors?.timeQuantity)}
                  helperText={errors?.timeQuantity}
                  onWheel={e => (e.target as HTMLInputElement).blur()}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <ArrowDropUpIcon sx={{ marginBottom: '-5px', cursor: 'pointer' }} onClick={incrementDay} />
                          <ArrowDropDownIcon sx={{ marginTop: '-5px', cursor: 'pointer' }} onClick={decrementDay} />
                        </div>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {medicalCertificate && medicalCertificate?.alerts?.find(alerta => alerta === 'INSS') && (
                <Grid item xs={12} md={6} sx={{ pl: 1 }}>
                  <TextField
                    fullWidth
                    type="number"
                    placeholder="Dias justificados"
                    label="Dias justificados (0 a 15 dias)"
                    value={medicalCertificate.justifiedDays ?? ''}
                    onWheel={e => (e.target as HTMLInputElement).blur()}
                    onChange={e => {
                      if (Number(e.target.value) >= 0 && Number(e.target.value) <= 15) {
                        handleJustifiedDays(e.target.value);
                      }
                    }}
                    error={Boolean(errors?.justifiedDays)}
                    helperText={errors?.justifiedDays}
                  />
                </Grid>
              )}
            </Grid>
          ) : (
            <>
              {formatForRecordingNumberOfHoursId === 1 && (
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
                >
                  <Grid item xs={12} md={6}>
                    <FormControl error={Boolean(errors?.timeQuantity)} fullWidth>
                      <TimePicker
                        value={`${medicalCertificate.startTime}`}
                        label="Hora inicial"
                        onChange={e => {
                          handleStartTime(`${e}`);
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            error={Boolean(errors?.startTime)}
                            helperText={errors?.startTime}
                            placeholder="Selecione o início"
                          />
                        )}
                      />
                      <FormHelperText>{errors?.timeQuantity}</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl error={Boolean(errors?.timeQuantity)} fullWidth>
                      <TimePicker
                        value={medicalCertificate.endTime}
                        label="Hora final"
                        minTime={moment(medicalCertificate.startTime)}
                        onChange={e => {
                          handleEndTime(`${e}`);
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            error={Boolean(errors?.endTime)}
                            placeholder="Selecione o fim"
                            helperText={errors?.endTime}
                          />
                        )}
                      />
                      <FormHelperText>{errors?.timeQuantity}</FormHelperText>
                    </FormControl>
                  </Grid>
                </LocalizationProvider>
              )}
              {formatForRecordingNumberOfHoursId === 2 && (
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
                >
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <TimePicker
                        value={startTimer}
                        label="Hora inicial"
                        onChange={e => {
                          setStartTimer(e);
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            error={Boolean(errors?.startTime)}
                            helperText={errors?.startTime}
                            placeholder="Selecione o início"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputMask
                        mask={'99:99'}
                        value={timerQuantity}
                        onChange={e => {
                          setTimerQuantity(e.target.value);
                          handleChangeTimeQuantity(String(e.target.value));
                        }}
                        maskPlaceholder={null}
                      >
                        <TextField label="Quantidade de horas" placeholder="00:00" autoComplete="off" />
                      </InputMask>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <TimePicker
                        value={endTimer}
                        label="Hora final"
                        onChange={e => {
                          setEndTimer(e);
                        }}
                        minTime={startTimer}
                        disabled
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            error={Boolean(errors?.endTimer)}
                            placeholder="Selecione o fim"
                            helperText={errors?.endTimer}
                            onKeyUpCapture={e => {
                              focus.setFocus(e, buttonRef);
                            }}
                            disabled
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                </LocalizationProvider>
              )}
            </>
          )}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <InputMask
              mask={'99 99999-9999'}
              value={medicalCertificate.phoneNumber}
              onChange={e => setMedicalCertificate({ ...medicalCertificate, phoneNumber: e.target.value })}
              maskPlaceholder={null}
              style={{ marginBottom: '5px' }}
            >
              <TextField
                fullWidth
                label="Telefone"
                placeholder="Telefone do colaborador para atualizações sobre o andamento do atestado"
                sx={{ marginTop: '15px' }}
                error={Boolean(errors?.phoneNumber)}
                helperText={errors?.phoneNumber}
              />
            </InputMask>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="E-mail"
              placeholder="E-mail do colaborador para atualizações sobre o andamento do atestado"
              sx={{ marginTop: '15px' }}
              value={medicalCertificate.email}
              onChange={e => setMedicalCertificate({ ...medicalCertificate, email: e.target.value })}
              error={Boolean(errors?.email)}
              helperText={errors?.email}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Observação"
              placeholder="Observação"
              value={medicalCertificate.notes}
              onChange={e => setMedicalCertificate({ ...medicalCertificate, notes: e.target.value })}
              error={Boolean(errors?.notes)}
              helperText={errors?.notes}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              disabled
              label="Observação do Colaborador"
              placeholder="Observação do Colaborador"
              value={medicalCertificate.comments}
              onChange={e => setMedicalCertificate({ ...medicalCertificate, notes: e.target.value })}
              error={Boolean(errors?.comments)}
              helperText={errors?.comments}
            />
          </Grid>
        </Grid>
        {medicalCertificate.id && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <MedicalCertificateFormCid medicalCertificateId={`${medicalCertificate.id}`} />
            </Grid>
            <Grid item xs={12} md={6}>
              <MedicalCertificateFormAttachments medicalCertificateId={`${medicalCertificate.id}`} />
            </Grid>
          </Grid>
        )}

        <Grid container spacing={2} sx={{ marginTop: 1 }}>
          <Grid item xs={12} md={12}>
            <LoadingButton onClick={handleSubmit} variant="contained" loading={loading}>
              Salvar
            </LoadingButton>
          </Grid>
        </Grid>
      </Form>
    </Container>
  );
};

export default MedicalCertificateForm;
